import React from 'react';
import cn from 'classnames';

import Checkbox from '../Checkbox';
import { PresetTemplateSummary } from 'src/types/TShare';

import styles from './PresetPicker.module.scss';

// Plain-English chips for the descriptive `features` list.
const FEATURE_LABELS: Record<string, string> = {
  feedback: 'Comments',
  approval: 'Approval',
  watermark: 'Watermarked',
  original: 'Originals',
  'high-res': 'High-Res',
  'web-optimized': 'Web-Optimized',
  'social-sizes': 'Social Sizes',
  streaming: 'Streaming',
  'long-expiry': 'Long Expiry',
  'preview-only': 'Preview Only',
  'multi-quality': 'Multi-Quality',
  'multiple-sizes': 'Multiple Sizes',
  print: 'Print-ready',
};

export type PresetPickerProps = {
  presets: PresetTemplateSummary[];
  selected: Set<string>;
  onToggle: (presetId: string) => void;
  /** Whether the Print Export addon is enabled (drives addon hints). */
  printExportAvailable?: boolean;
};

/**
 * Flat multi-select grid of share presets. Replaces the old category-grouped
 * markup that was duplicated in PresetTemplatesModal and TemplatesList; the
 * preset catalog is now a flat purpose list with no category taxonomy.
 */
export const PresetPicker: React.FC<PresetPickerProps> = ({
  presets,
  selected,
  onToggle,
  printExportAvailable = false,
}) => (
  <div className={styles.presetsGrid}>
    {presets.map((preset) => {
      const addonMissing =
        preset.requires_addon === 'PRINT_EXPORT' && !printExportAvailable;
      return (
        <div
          key={preset.preset_id}
          className={cn(styles.presetCard, {
            [styles.selected]: selected.has(preset.preset_id),
          })}
          onClick={() => onToggle(preset.preset_id)}
        >
          <div className={styles.presetHeader}>
            <Checkbox
              checked={selected.has(preset.preset_id)}
              onChange={() => onToggle(preset.preset_id)}
            />
            <span className={styles.presetName}>{preset.name}</span>
          </div>
          <p className={styles.presetDescription}>{preset.description}</p>
          {addonMissing && (
            <span className={styles.addonHint}>Requires the Print Export addon</span>
          )}
          <div className={styles.presetMeta}>
            <span className={styles.expiryBadge}>
              Share expires in {preset.default_expiry_days} days
            </span>
            <div className={styles.featureBadges}>
              {preset.features.slice(0, 3).map((feature) => (
                <span key={feature} className={styles.featureBadge}>
                  {FEATURE_LABELS[feature] || feature}
                </span>
              ))}
              {preset.features.length > 3 && (
                <span className={styles.featureBadge}>
                  +{preset.features.length - 3}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

export default PresetPicker;
